import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useShortlist } from "@/context/ShortlistContext";
import { api, mediaUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import BookingForm from "@/components/BookingForm";
import VoiceCard from "@/components/VoiceCard";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Heart, ArrowRight } from "lucide-react";

export default function Shortlist() {
  const { items, clear } = useShortlist();
  const [talents, setTalents] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const ids = useMemo(() => items.map((i) => i.id).join(","), [items]);

  // Fetch full talent data (with demos) for each favorite so audio playback works.
  useEffect(() => {
    if (items.length === 0) { setTalents([]); return; }
    let alive = true;
    Promise.all(items.map((it) => api.get(`/voices/${it.id}`).then((r) => r.data).catch(() => it)))
      .then((full) => { if (alive) setTalents(full); });
    return () => { alive = false; };
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep every favorite selected by default; drop ids that no longer exist.
  useEffect(() => {
    setSelectedIds((prev) => {
      const all = items.map((i) => i.id);
      const kept = prev.filter((id) => all.includes(id));
      return kept.length ? kept : all;
    });
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const startBooking = (onlyId) => {
    if (onlyId) setSelectedIds([onlyId]);
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const selectedCount = selectedIds.length;

  return (
    <>
      <SEO title="Jouw favorieten" path="/shortlist" description="Bekijk je opgeslagen kinderstemmen en start een boekingsaanvraag." />
      <section className="bg-brand-ink text-white">
        <div className="container-x py-14 md:py-16">
          <span className="label-eyebrow text-brand-purple">Favorieten</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter md:text-5xl">Jouw favorieten ({items.length})</h1>
        </div>
      </section>

      {items.length === 0 && !showForm ? (
        <section className="container-x py-24 text-center">
          <Heart size={40} className="mx-auto mb-4 text-zinc-300" />
          <p className="font-semibold text-zinc-700">Je favorietenlijst is leeg.</p>
          <p className="text-zinc-500">Bekijk stemmen en tik op het hartje om ze hier toe te voegen.</p>
          <Link to="/kinderstemmen" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-3 font-bold text-white transition hover:bg-brand-violetHover">Bekijk stemmen <ArrowRight size={16} /></Link>
        </section>
      ) : (
        <section className="container-x section-y">
          {!submitted && (
          <>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-zinc-600">Selecteer de stem(men) waarvoor je een boekingsaanvraag wilt starten.</p>
            <button onClick={clear} data-testid="clear-shortlist" className="text-sm font-semibold text-zinc-400 hover:text-red-500">Alles wissen</button>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {talents.map((t) => {
              const on = selectedIds.includes(t.id);
              return (
                <div key={t.id} data-testid={`favorite-${t.id}`}
                  className={`rounded-2xl border-2 p-1.5 transition ${on ? "border-brand-violet" : "border-transparent"}`}>
                  <label className="mb-1.5 flex cursor-pointer items-center gap-2.5 px-2 pt-1.5 text-sm font-semibold text-zinc-700">
                    <Checkbox checked={on} onCheckedChange={() => toggle(t.id)} data-testid={`favorite-check-${t.id}`} />
                    Selecteren voor boeking
                  </label>
                  <VoiceCard talent={t} />
                  <button onClick={() => startBooking(t.id)} data-testid={`start-booking-${t.id}`}
                    className="mt-2 w-full rounded-xl border border-brand-violet px-4 py-2.5 text-sm font-bold text-brand-violet transition hover:bg-brand-violet hover:text-white">
                    Boekingsaanvraag starten
                  </button>
                </div>
              );
            })}
          </div>

          {/* Primary CTA */}
          {!showForm && (
            <div className="mt-8 flex justify-center">
              <button onClick={() => startBooking(null)} disabled={selectedCount === 0} data-testid="start-booking-primary"
                className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-8 py-4 font-bold text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover disabled:opacity-50">
                Boekingsaanvraag voor {selectedCount} {selectedCount === 1 ? "stem" : "stemmen"} <ArrowRight size={18} />
              </button>
            </div>
          )}
          </>
          )}

          {/* Booking form */}
          {showForm && (
            <div ref={formRef} className={`rounded-3xl border border-zinc-200 bg-white p-6 md:p-8 ${submitted ? "" : "mt-12"}`} data-testid="booking-form-panel">
              {!submitted && <>
                <h2 className="mb-1 font-heading text-2xl font-bold">Boekingsaanvraag</h2>
                <p className="mb-6 text-zinc-600">Nog geen definitieve boeking — we controleren eerst de beschikbaarheid en prijs.</p>
              </>}
              <BookingForm talents={talents} selectedIds={selectedIds} onSelectedChange={setSelectedIds} onSubmitted={() => setSubmitted(true)} />
            </div>
          )}
        </section>
      )}
    </>
  );
}
